import React, { useState, useEffect } from 'react';
import { BarChart3, RotateCw } from 'lucide-react';
import { agentService } from '../API/agentService';
import './ProjectOverview.css';

const ProjectOverview = ({ projects, agents, onRefresh }) => {
  const [platformStats, setPlatformStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    calculatePlatformStats();
    loadRecentActivity();
  }, [projects, agents]);

  const calculatePlatformStats = () => {
    if (!projects.length) return;

    const totalProjects = projects.length;
    const activeProjects = projects.filter(p => p.status === 'active').length;
    const overallCompletion = Math.round(
      projects.reduce((sum, p) => sum + p.completionPercentage, 0) / totalProjects
    );
    const averageVelocity = Math.round(
      projects.reduce((sum, p) => sum + (p.velocityTarget || 0), 0) / totalProjects
    );

    setPlatformStats({
      totalProjects,
      activeProjects,
      overallCompletion,
      averageVelocity,
      activeAgents: agents.filter(a => a.status === 'active').length,
      totalAgents: agents.length
    });
  };

  const loadRecentActivity = async () => {
    try {
      const reports = await agentService.getReports('day');
      setRecentActivity(reports.slice(0, 5)); // Show last 5 activities
    } catch (error) {
      console.error('Failed to load recent activity:', error);
    }
  };

  const handleRunStandup = async () => {
    setLoading(true);
    try {
      await agentService.runStandup();
      onRefresh();
    } catch (error) {
      console.error('Failed to run standup:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectStatusColor = (project) => {
    if (project.completionPercentage >= 80) return '#f59e0b'; // Gold for success/achievement
    if (project.completionPercentage >= 50) return '#26a69a'; // Teal for progress
    return '#4290f5'; // Azure for information/getting started
  };

  const getProgressBarClass = (project) => {
    if (project.completionPercentage >= 80) return 'achievement'; // Gold gradient
    if (project.completionPercentage >= 95) return 'celebration'; // Rainbow celebration
    return ''; // Default teal gradient
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  return (
    <div className="project-overview">
      {/* Platform Statistics */}
      <div className="grid grid-4">
        <div className="dashboard-card">
          <div className="stat-content">
            <div className="stat-number">{platformStats.overallCompletion}%</div>
            <div className="stat-label">Platform Completion</div>
            <div className="stat-detail">{platformStats.activeProjects} active projects</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="stat-content">
            <div className="stat-number">{platformStats.averageVelocity}</div>
            <div className="stat-label">Avg. Velocity</div>
            <div className="stat-detail">points per day</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="stat-content">
            <div className="stat-number">{platformStats.activeAgents}</div>
            <div className="stat-label">Active Agents</div>
            <div className="stat-detail">of {platformStats.totalAgents} total</div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="stat-content">
            <button
              className="btn btn-primary"
              onClick={handleRunStandup}
              disabled={loading}
              aria-label={loading ? 'Running daily standup...' : 'Run daily standup'}
            >
              {loading ? <RotateCw className="animate-spin" size={16} /> : <BarChart3 size={16} />}
              <span>Daily Standup</span>
            </button>
            <div className="stat-detail">Run platform status report</div>
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        {/* Active Projects */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Active Projects</h3>
            <p className="card-subtitle">Current development focus</p>
          </div>

          <div className="projects-list">
            {projects.map(project => (
              <div key={project.id} className="project-item">
                <div className="project-header">
                  <div className="project-info">
                    <h4 className="project-name">{project.name}</h4>
                    <span className={`status-badge ${project.status}`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="project-completion">
                    {project.completionPercentage}%
                  </div>
                </div>

                <div className="project-details">
                  <div className="project-phase">
                    Phase: <strong>{project.currentPhase || 'Planning'}</strong>
                  </div>
                  <div className="project-velocity">
                    Velocity: {project.velocityTarget} pts/day
                  </div>
                </div>

                <div className="progress-bar">
                  <div
                    className={`progress-fill ${getProgressBarClass(project)}`}
                    style={{
                      width: `${project.completionPercentage}%`
                    }}
                    role="progressbar"
                    aria-valuenow={project.completionPercentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label={`${project.name} completion: ${project.completionPercentage}%`}
                  />
                </div>

                <div className="project-description">
                  {project.description}
                </div>
              </div>
            ))}

            {projects.length === 0 && (
              <div className="empty-state">
                <p>No projects found. Create your first project to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* Agent Status */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Agent Status</h3>
            <p className="card-subtitle">Real-time monitoring</p>
          </div>

          <div className="agents-list">
            {agents.map(agent => (
              <div key={agent.id} className="agent-item">
                <div className="agent-header">
                  <div className="agent-info">
                    <h4 className="agent-name">{agent.name}</h4>
                    <span className={`status-badge ${agent.status}`}>
                      {agent.status}
                    </span>
                  </div>
                  <div className="agent-last-run">
                    {formatTimeAgo(agent.lastRun)}
                  </div>
                </div>

                <div className="agent-description">
                  {agent.description}
                </div>

                {agent.nextScheduled && (
                  <div className="agent-next-run">
                    Next: {formatTimeAgo(agent.nextScheduled)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Recent Activity</h3>
          <p className="card-subtitle">Latest agent reports and updates</p>
        </div>

        <div className="activity-list">
          {recentActivity.map(activity => (
            <div key={activity.id} className="activity-item">
              <div className="activity-header">
                <div className="activity-info">
                  <span className="activity-type">{activity.type}</span>
                  <span className="activity-project">{activity.project}</span>
                </div>
                <div className="activity-time">
                  {formatTimeAgo(activity.timestamp)}
                </div>
              </div>
              <div className="activity-agent">
                by {activity.agent}
              </div>
            </div>
          ))}

          {recentActivity.length === 0 && (
            <div className="empty-state">
              <p>No recent activity. Run agents to see reports here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectOverview;